import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRunning } from "@fortawesome/free-solid-svg-icons";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { db } from "@lib/firebase";
import { doc, getDoc } from "firebase/firestore";
import useEmblaCarousel from "embla-carousel-react";

const getCategoryIcons = (categoryName, distance) => {
  const normName = categoryName.trim().toUpperCase();

  if (normName.includes("FUN TRAIL") || distance == 5) {
    return {
      icon1Url: "/icons/utmb.png",
      icon2Url: "/icons/itra0.svg",
      icon3Url: null
    };
  } else if (normName.includes("CIANJUR TRAIL RUN") || distance == 10) {
    return {
      icon1Url: "/icons/utmb.png",
      icon2Url: "/icons/itra0.svg",
      icon3Url: null
    };
  } else if (normName.includes("HM TRAIL") || distance == 21) {
    return {
      icon1Url: "/icons/utmb.png",
      icon2Url: "/icons/utmb20.png",
      icon3Url: "/icons/itra1.png"
    };
  } else if (normName.includes("MOUNTAIN MARATHON") || distance == 42) {
    return {
      icon1Url: "/icons/utmb.png",
      icon2Url: "/icons/utmb50.png",
      icon3Url: "/icons/itra3.png"
    };
  } else if (normName.includes("ULTRA TRAIL") || distance == 75) {
    return {
      icon1Url: "/icons/utmb.png",
      icon2Url: "/icons/utmb100.svg",
      icon3Url: "/icons/itra4.png"
    };
  }

  return { icon1Url: null, icon2Url: null, icon3Url: null };
};


const parsePrice = (price) => {
  if (!price) return 0;
  if (typeof price === "number") return price;
  const str = String(price).toUpperCase().trim();
  if (str.includes("M")) {
    return Number(str.replace("M", "")) * 1000000;
  }
  if (str.includes("K")) {
    return Number(str.replace("K", "")) * 1000;
  }
  return Number(str.replace(/[^0-9.]/g, "")) || 0;
};

const formatPrice = (price) => {
  const num = parsePrice(price);
  return num > 0 ? `IDR ${num.toLocaleString("en-US")}` : "-";
};

const isRegistrationOpenStatus = (status) => {
  if (!status) return false;
  const normalized = status.trim().toLowerCase();
  return [
    "registration open",
    "open",
    "pendaftaran dibuka",
    "registrasi dibuka",
  ].includes(normalized);
};

const getCategoryImage = (categoryName) => {
  const normName = categoryName.trim().toUpperCase();
  
  if (normName.includes("FUN TRAIL") || normName.includes("5")) return "/card/5k.jpg";
  if (normName.includes("CIANJUR TRAIL RUN") || normName.includes("10")) return "/card/10k.jpg";
  if (normName.includes("HM TRAIL") || normName.includes("21")) return "/card/21k.jpg";
  if (normName.includes("MOUNTAIN MARATHON") || normName.includes("42")) return "/card/42k.jpg";
  if (normName.includes("ULTRA TRAIL") || normName.includes("75")) return "/card/75k.jpg";
  
  return "/bg/bghero11.jpg";
};

// Fungsi helper untuk check tanggal (otomatis tutup jika lewat hari ini)
const isEventInFuture = (eventDateStr) => {
  if (!eventDateStr) return false;
  try {
    const eventDate = new Date(eventDateStr);
    const now = new Date();
    now.setHours(0, 0, 0, 0); // Reset jam untuk perbandingan harian
    eventDate.setHours(0, 0, 0, 0);
    return eventDate > now;
  } catch {
    return false;
  }
};

export const SectionKategori = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [emblaRef] = useEmblaCarousel({
    loop: false,
    align: "start",
    dragFree: true,
    containScroll: "trimSnaps",
  });

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const eventRef = doc(db, "semuaKegiatan", "JOTR2026");
        const eventDoc = await getDoc(eventRef);

        if (eventDoc.exists()) {
          const data = eventDoc.data();
          const rawCategories = data.categories || [];

          // Ambil status dari level event
          const eventStatus = data.status || "Registration Closed";
          const isEventOpen = isRegistrationOpenStatus(eventStatus);

          // Map categories with explicit order and filter out Vertical (7K)
          const mappedCategories = rawCategories
            .filter((cat) => cat.categoryName.trim() !== "EXCLUDE_ME") // Cleaned filter
            .map((cat, index) => {
              const trimmedCategoryName = cat.categoryName.trim();
              const upperName = trimmedCategoryName.toUpperCase();
              let order;
              
              if (upperName.includes("FUN TRAIL")) order = 1;
              else if (upperName.includes("CIANJUR TRAIL RUN") || upperName.includes("10")) order = 2;
              else if (upperName.includes("HM TRAIL") || upperName.includes("21")) order = 3;
              else if (upperName.includes("MOUNTAIN MARATHON") || upperName.includes("42")) order = 4;
              else if (upperName.includes("ULTRA TRAIL") || upperName.includes("75")) order = 5;
              else order = 6 + index;

              const mappedCategory = {
                distance: cat.distance.toString(),
                categoryName: trimmedCategoryName,
                elevationGain: cat.elevationGain,
                cot: cat.cot,
                price: cat.price,
                foreignerPrice: cat.foreignerPrice || cat.price, // Fallback to price if foreignerPrice is not set
                // Pakai status dari setiap category + event status
                isRegistrationOpen: cat.isRegistrationOpen === true || (isEventOpen && cat.isRegistrationOpen !== false),
                order,
              };
              return mappedCategory;
            });

          // Sort by order
          const sortedCategories = mappedCategories.sort(
            (a, b) => a.order - b.order
          );

          setCategories(sortedCategories);
        } else {
          setError("Jakarta Open Trail Run document not found");
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // Deteksi lebar layar untuk mengaktifkan Embla Carousel di bawah 1024px
  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 1024);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (loading || error) {
    return (
      <section id="category" className="w-full py-20 bg-lime-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto text-center">
            <p className="text-slate-600 text-lg">
              {loading ? "Loading categories..." : error || "An error occurred"}
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="category"
      className="w-full py-20 relative z-0 bg-cover bg-center bg-fixed"
      style={{
        backgroundImage: "url('/bg/bghero11.jpg')",
      }}
    >
      <div className="absolute inset-0 bg-lime-50/90 z-0"></div>
      <div className="container mx-auto px-2 sm:px-4 lg:px-6 relative z-10">
        <div className="max-w-[1440px] mx-auto">
          <div className="text-center mb-8">
            <motion.h2
              className="text-2xl font-black uppercase tracking-wider text-lime-600"
              initial={{ opacity: 0, y: -15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            >
              RACE CATEGORIES
            </motion.h2>
            <motion.h3
              className="text-3xl md:text-5xl font-black text-lime-600 mt-2"
              initial={{ opacity: 0, y: -15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
            >
             JOTR CIANJUR SERIES
            </motion.h3>
          </div>

          {isSmallScreen ? (
            <div className="overflow-hidden px-2 sm:px-4" ref={emblaRef}>
              <div className="flex flex-col gap-4 sm:flex-row sm:gap-4">
                {categories.map((category, index) => {
                  const icons = getCategoryIcons(
                    category.categoryName,
                    category.distance
                  );
                  const categoryImage = getCategoryImage(category.categoryName);
                  return (
                    <Link
                      key={`${category.distance}-${category.categoryName}`}
                      to={`/JOTR2026/${
                        category.categoryName === "Mix Group (20K)"
                          ? "20-group"
                          : category.categoryName === "Fun Trail (7K)"
                          ? "7-fun"
                          : category.distance
                      }`}
                      className="group relative z-0 min-w-[90%] w-full sm:min-w-[240px] sm:flex-1 sm:flex-shrink-0"
                    >
                      <motion.div
                        className="relative rounded-2xl overflow-hidden bg-cover bg-center w-full h-72 z-0"
                        style={{
                          backgroundImage: `url(${categoryImage})`,
                          aspectRatio: "2/1",
                        }}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          duration: 0.5,
                          delay: index * 0.1,
                          ease: "easeOut",
                        }}
                      >
                        <div
                          className="relative flex flex-col items-center justify-center h-full
                          bg-gradient-to-t from-lime-600/100 to-lime-600/20
                          gap-6 transition-all duration-300 group-hover:opacity-0 z-0"
                        >
                          <div className="flex flex-col items-center">
                            <h3 className="text-3xl font-black mt-2 mb-1 text-white uppercase z-10 text-center">
                              {category.categoryName === "Mix Group (20K)"
                                ? "Group Mix"
                                : category.categoryName === "Fun Trail (7K)"
                                ? "Fun Trail"
                                : `${category.distance} KM`}
                            </h3>
                            <p className="text-xs font-bold text-white uppercase text-center">
                              {category.categoryName}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="text-center">
                              <p className="text-xs text-white/80">ELEVATION</p>
                              <p className="text-xs font-bold text-white whitespace-nowrap">
                                {category.elevationGain || "-"}M+
                              </p>
                            </div>
                            <div className="h-6 w-px bg-white/20"></div>
                            <div className="text-center">
                              <p className="text-xs text-white/80">CUT OFF</p>
                              <p className="text-xs font-bold text-white whitespace-nowrap">
                                {category.cot ? `${category.cot} HOURS` : "- HOURS"}
                              </p>
                            </div>
                            <div className="h-6 w-px bg-white/20"></div>
                            <div className="text-center">
                              <p className="text-xs text-white/80">PRICE</p>
                              <p className="text-xs font-bold text-white whitespace-nowrap">
                                IDR{" "}
                                {parsePrice(category.price).toLocaleString("en-US")}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            {[icons.icon1Url, icons.icon2Url, icons.icon3Url]
                              .filter(Boolean)
                              .sort((a, b) => (a.includes("itra") ? 0 : 1) - (b.includes("itra") ? 0 : 1))
                              .map((url, i) => (
                                <img
                                  key={i}
                                  src={url}
                                  alt="Category badge"
                                  className="h-5 object-contain"
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                  }}
                                />
                              ))}
                          </div>
                          <div className="text-center">
                            <p className="text-xs font-bold text-white uppercase">
                              {category.isRegistrationOpen
                                ? "REGISTRATION OPEN"
                                : "REGISTRATION CLOSED"}
                            </p>
                          </div>
                        </div>
                        <div className="absolute inset-0 flex items-center justify-center bg-lime-600 bg-opacity-80 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-0">
                          <p className="text-lg font-normal uppercase text-white">
                            View Details
                          </p>
                        </div>
                      </motion.div>
                    </Link>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-4 py-10 px-2 sm:flex-row sm:flex-nowrap sm:justify-center sm:px-4 lg:px-6">
              {categories.map((category, index) => {
                const icons = getCategoryIcons(
                  category.categoryName,
                  category.distance
                );
                const categoryImage = getCategoryImage(category.categoryName);
                return (
                  <Link
                    key={`${category.distance}-${category.categoryName}`}
                    to={`/JOTR2026/${
                      category.categoryName === "Mix Group (20K)"
                        ? "20-group"
                        : category.categoryName === "Fun Trail (7K)"
                        ? "7-fun"
                        : category.distance
                    }`}
                    className="group relative z-0 min-w-[90%] w-full sm:min-w-[240px] sm:flex-1 sm:flex-shrink-0"
                  >
                    <motion.div
                      className="relative rounded-2xl overflow-hidden bg-cover bg-center w-full h-72 z-0"
                      style={{
                        backgroundImage: `url(${categoryImage})`,
                        aspectRatio: "2/1",
                      }}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        duration: 0.5,
                        delay: index * 0.1,
                        ease: "easeOut",
                      }}
                    >
                      <div
                        className="relative flex flex-col items-center justify-center h-full
                          bg-gradient-to-t from-lime-600/100 to-lime-600/20
                          gap-6 transition-all duration-300 group-hover:opacity-0 z-0"
                      >
                        <div className="flex flex-col items-center">
                          <h3 className="text-3xl font-black mt-2 mb-1 text-white uppercase z-10 text-center">
                            {category.categoryName === "Mix Group (20K)"
                              ? "Group Mix"
                              : category.categoryName === "Fun Trail (7K)"
                              ? "Fun Trail"
                              : `${category.distance} KM`}
                          </h3>
                          <p className="text-xs font-bold text-white uppercase text-center">
                            {category.categoryName}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="text-center">
                            <p className="text-xs text-white/80">ELEVATION</p>
                            <p className="text-xs font-bold text-white whitespace-nowrap">
                              {category.elevationGain || "-"}M+
                            </p>
                          </div>
                          <div className="h-6 w-px bg-white/20"></div>
                          <div className="text-center">
                            <p className="text-xs text-white/80">CUT OFF</p>
                            <p className="text-xs font-bold text-white whitespace-nowrap">
                              {category.cot ? `${category.cot} HOURS` : "- HOURS"}
                            </p>
                          </div>
                          <div className="h-6 w-px bg-white/20"></div>
                          <div className="text-center">
                            <p className="text-xs text-white/80">PRICE</p>
                            <p className="text-xs font-bold text-white whitespace-nowrap">
                              IDR{" "}
                              {parsePrice(category.price).toLocaleString("en-US")}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {[icons.icon1Url, icons.icon2Url, icons.icon3Url]
                            .filter(Boolean)
                            .sort((a, b) => (a.includes("itra") ? 0 : 1) - (b.includes("itra") ? 0 : 1))
                            .map((url, i) => (
                              <img
                                key={i}
                                src={url}
                                alt="Category badge"
                                className="h-5 object-contain"
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                            ))}
                        </div>
                        <div className="text-center">
                          <p className="text-xs font-bold text-white uppercase">
                            {category.isRegistrationOpen
                              ? "REGISTRATION OPEN"
                              : "REGISTRATION CLOSED"}
                          </p>
                        </div>
                      </div>
                      <div className="absolute inset-0 flex items-center justify-center bg-lime-600 bg-opacity-80 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-0">
                        <p className="text-lg font-normal uppercase text-white">
                          View Details
                        </p>
                      </div>
                    </motion.div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
